
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CodeEditor } from './CodeEditor';

export function WebhookEventTypes() {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [eventCode, setEventCode] = useState('');
  const [description, setDescription] = useState('');
  const [availableVariables, setAvailableVariables] = useState('{}');
  const [isActive, setIsActive] = useState(true);

  // Query event types
  const { data: eventTypes, isLoading } = useQuery({
    queryKey: ['webhook-event-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_event_types')
        .select('*')
        .order('event_code', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Create or update event type
  const mutation = useMutation({
    mutationFn: async (values: any) => {
      if (editItem) {
        // Update
        const { data, error } = await supabase
          .from('webhook_event_types')
          .update({
            event_code: values.eventCode,
            description: values.description,
            available_variables: values.availableVariables,
            is_active: values.isActive,
          })
          .eq('event_type_id', editItem.event_type_id)
          .select();
        
        if (error) throw error;
        return data;
      } else {
        // Create
        const { data, error } = await supabase
          .from('webhook_event_types')
          .insert({
            event_code: values.eventCode,
            description: values.description,
            available_variables: values.availableVariables,
            is_active: values.isActive,
          })
          .select();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-event-types'] });
      toast({
        title: `Event Type ${editItem ? 'Updated' : 'Created'}`,
        description: `Successfully ${editItem ? 'updated' : 'created'} webhook event type.`,
      });
      resetForm();
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reset form
  const resetForm = () => {
    setEventCode('');
    setDescription('');
    setAvailableVariables('{}');
    setIsActive(true);
    setEditItem(null);
  };

  // Open dialog for editing
  const handleEdit = (item: any) => {
    setEditItem(item);
    setEventCode(item.event_code);
    setDescription(item.description);
    setAvailableVariables(JSON.stringify(item.available_variables, null, 2));
    setIsActive(item.is_active);
    setOpen(true);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let parsedVariables;
    try {
      parsedVariables = JSON.parse(availableVariables);
    } catch (error) {
      toast({
        title: 'Invalid JSON',
        description: 'Available variables must be valid JSON.',
        variant: 'destructive',
      });
      return;
    }

    mutation.mutate({
      eventCode,
      description,
      availableVariables: parsedVariables,
      isActive,
    });
  };

  // Handle adding a new event type
  const handleAddNew = () => {
    resetForm();
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Webhook Event Types</CardTitle>
        <Button onClick={handleAddNew}>Add Event Type</Button>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventTypes?.map((item) => (
                  <TableRow key={item.event_type_id}>
                    <TableCell className="font-medium">{item.event_code}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <span className="text-green-500">Active</span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit' : 'Add'} Event Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="event-code">Event Code</Label>
                <Input 
                  id="event-code" 
                  value={eventCode} 
                  onChange={(e) => setEventCode(e.target.value)}
                  placeholder="order.created"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Triggered when a new order is created"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="available-variables">Available Variables (JSON)</Label>
                <CodeEditor
                  value={availableVariables}
                  onChange={setAvailableVariables}
                  language="json"
                  height="200px"
                />
                <p className="text-sm text-muted-foreground">
                  Define the variables that are available in this event.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch 
                  id="is-active" 
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="is-active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
